import type { ReactNode } from 'react';

/**
 * LO PURO DE `DataTable` (tanda 6): repartir las filas en grupos, marcar un grupo entero y
 * armar el CSV. Vive aparte para poder probarse sin pintar nada (y porque un archivo de
 * componentes no exporta funciones sueltas).
 */

export interface Agrupacion<T> {
  /** A qué grupo va cada fila. */
  de: (fila: T) => string;
  /** Los grupos en el orden en que se dibujan (también los vacíos, si `mostrarVacios`). */
  grupos: Array<{ clave: string; etiqueta: string; /** Clase de Tailwind (un fondo) para la franja. */ color?: string }>;
  /** Pie del grupo (por ejemplo, cuántos y cuánto suman). */
  pie?: (filas: T[]) => ReactNode;
  mostrarVacios?: boolean;
}

export interface GrupoDeFilas<T> {
  clave: string;
  etiqueta: string;
  color?: string;
  filas: T[];
}

/** La clave del grupo de las filas que no caben en ninguno de los declarados. */
export const SIN_GRUPO = '__sin_grupo__';

/**
 * Las filas repartidas en sus grupos, en el orden de `grupos` y, dentro de cada uno, en el orden
 * en que vienen (así el orden por columna vale dentro del grupo). Una fila cuyo grupo no está
 * declarado va a un último grupo «Sin grupo»: nunca se pierde. Los grupos vacíos no salen, salvo
 * con `mostrarVacios`.
 */
export function repartirEnGrupos<T>(filas: readonly T[], { de, grupos, mostrarVacios }: Pick<Agrupacion<T>, 'de' | 'grupos' | 'mostrarVacios'>): GrupoDeFilas<T>[] {
  const porClave = new Map<string, GrupoDeFilas<T>>();
  for (const g of grupos) if (!porClave.has(g.clave)) porClave.set(g.clave, { ...g, filas: [] });
  const sueltas: T[] = [];
  for (const fila of filas) (porClave.get(de(fila))?.filas ?? sueltas).push(fila);
  const repartidas = [...porClave.values()].filter((g) => mostrarVacios || g.filas.length > 0);
  return sueltas.length > 0 ? [...repartidas, { clave: SIN_GRUPO, etiqueta: 'Sin grupo', filas: sueltas }] : repartidas;
}

/** La selección tras tocar la casilla de un grupo: si ya estaban todas las suyas, las quita; si no, las marca todas. */
export function marcarGrupo(seleccion: ReadonlySet<string>, ids: readonly string[]): Set<string> {
  const nueva = new Set(seleccion);
  const todas = ids.length > 0 && ids.every((id) => seleccion.has(id));
  for (const id of ids) {
    if (todas) nueva.delete(id);
    else nueva.add(id);
  }
  return nueva;
}

/**
 * Las filas como CSV, una columna por cada una que tenga `valor`… o vacía si no lo tiene.
 * Punto y coma y BOM: es lo que abre bien Excel en español sin pedir nada.
 */
export function csvDe<T>(filas: readonly T[], columnas: ReadonlyArray<{ titulo: string; valor?: (fila: T) => string | number | null | undefined }>): string {
  const celda = (v: string | number | null | undefined) => {
    const texto = v == null ? '' : String(v);
    return /[",;\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
  };
  const lineas = filas.map((f) => columnas.map((c) => celda(c.valor ? c.valor(f) : '')));
  return '﻿' + [columnas.map((c) => celda(c.titulo)), ...lineas].map((l) => l.join(';')).join('\r\n');
}

/** Baja un CSV con la fecha de hoy en el nombre. */
export function descargarCsv(nombre: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nombre}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
